import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/admin/Layout';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import './Events.css';

const Events = () => {
  const [events,  setEvents]  = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(true);
  const [deleting,setDeleting]= useState(null);
  const toast = useToast();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const LIMIT = 15;

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/events', { params: { page, limit: LIMIT, search } })
      .then(r => { setEvents(r.data.events); setTotal(r.data.total); })
      .catch(() => toast.error('Failed to load events.'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (id, current) => {
    try {
      await api.patch(`/admin/events/${id}/toggle`);
      setEvents(prev => prev.map(e => e.id === id ? { ...e, is_visible: !current } : e));
      toast.success(`Event ${!current ? 'published' : 'hidden'}.`);
    } catch { toast.error('Failed to update visibility.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success('Event deleted.');
      load();
    } catch { toast.error('Failed to delete event.'); }
    finally { setDeleting(null); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <Layout>
    <div className="page-enter">
      <div className="page-header">
        <div>
          <h1 className="page-title">Events</h1>
          <p className="page-subtitle">{total} total events</p>
        </div>
        {hasPermission('events.create') && (
          <Link to="/admin/events/new" className="btn btn-primary">+ New Event</Link>
        )}
      </div>

      {/* Search */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ padding: '14px 20px' }}>
          <input
            type="search" className="form-control" placeholder="🔍 Search events…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ maxWidth: 320 }}
          />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ width: 32, height: 32, margin: 'auto' }}/></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}></th>
                    <th>Event</th>
                    <th>Sessions</th>
                    <th>Price</th>
                    <th>Venue</th>
                    <th>Registrants</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map(ev => (
                    <tr key={ev.id}>
                      <td>
                        {ev.image_url
                          ? <img src={`https://testapi.aiworldcommunity.com${ev.image_url}`} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }}/>
                          : <div style={{ width: 40, height: 40, borderRadius: 6, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📅</div>
                        }
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{ev.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>/{ev.slug}</div>
                      </td>
                      <td style={{ color: 'var(--text-muted)' }}>
                        {ev.next_session ? new Date(ev.next_session).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td>
                        <span className={`badge ${ev.is_free ? 'badge-free' : 'badge-paid'}`}>
                          {ev.is_free ? 'Free' : `${ev.currency} ${parseFloat(ev.price).toFixed(2)}`}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-primary" style={{ textTransform: 'capitalize' }}>{ev.venue_type}</span>
                      </td>
                      <td>
                        <Link to={`/admin/events/${ev.id}/registrants`} style={{ fontWeight: 600, color: 'var(--primary)' }}>
                          {ev.registrant_count}
                        </Link>
                      </td>
                      <td>
                        <span className={`badge ${ev.is_visible ? 'badge-success' : 'badge-danger'}`}>
                          {ev.is_visible ? 'Published' : 'Hidden'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {hasPermission('events.view') && (
                            <Link to={`/admin/events/${ev.id}/registrants`} className="btn btn-ghost btn-sm" title="View registrants">👥</Link>
                          )}
                          {hasPermission('events.edit') && (
                            <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/admin/events/${ev.id}/edit`)} title="Edit">✏</button>
                          )}
                          {hasPermission('events.toggle') && (
                            <button className="btn btn-ghost btn-sm" onClick={() => handleToggle(ev.id, ev.is_visible)} title={ev.is_visible ? 'Hide' : 'Publish'}>
                              {ev.is_visible ? '👁' : '🚫'}
                            </button>
                          )}
                          {hasPermission('events.delete') && (
                            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(ev.id)} disabled={deleting === ev.id} title="Delete">
                              {deleting === ev.id ? '…' : '🗑'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!events.length && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
                      No events found. {hasPermission('events.create') && <Link to="/admin/events/new">Create one →</Link>}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
                <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <span style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text-muted)' }}>Page {page} of {totalPages}</span>
                <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default Events;
